import React, { useState, useRef } from "react";
import { TermsModal, PrivacyPolicyModal, AppFooter } from "./LegalModals";
import { useLanguage } from "../utils/i18n";

const PLAN_COLORS = {
  free_trial: "#4f8ef7",
  basic:      "#38bdf8",
  pro:        "#ff69b4",
  expired:    "#ef4444",
};

const FOLDER_GRADIENTS = [
  "linear-gradient(135deg,#4f6ef7,#7c3aed)",
  "linear-gradient(135deg,#22c55e,#0ea5e9)",
  "linear-gradient(135deg,#f59e0b,#ef4444)",
  "linear-gradient(135deg,#ec4899,#8b5cf6)",
  "linear-gradient(135deg,#06b6d4,#22c55e)",
  "linear-gradient(135deg,#f97316,#eab308)",
  "linear-gradient(135deg,#6366f1,#06b6d4)",
  "linear-gradient(135deg,#14b8a6,#84cc16)",
];

function SubjectModal({ modal, allBooks, onSave, onClose }) {
  const [name, setName] = useState(modal.name);
  const [bookIds, setBookIds] = useState(modal.bookIds || []);
  const { t, lang } = useLanguage();

  const toggleBook = (id) =>
    setBookIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  return (
    <div className="bcm-overlay" onClick={onClose}>
      <div className="subject-modal" onClick={(e) => e.stopPropagation()}>
        <div className="bcm-header">
          <h2 className="bcm-title">
            {modal.mode === "create" ? t("newSubjectTitle") : t("editSubjectTitle")}
          </h2>
          <button className="bcm-close" onClick={onClose}>✕</button>
        </div>
        <div className="subject-modal-body">
          <label className="bcm-label">{t("subjectNameLabel")}</label>
          <input
            className="bcm-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && name.trim() && onSave(name, bookIds)}
            placeholder={t("subjectNamePlaceholder")}
            autoFocus
          />
          <label className="bcm-label" style={{ marginTop: 20 }}>
            {t("chooseBooksLabel")}{" "}
            <span className="bcm-hint">({bookIds.length} {lang === "zh" ? "本已选" : "selected"})</span>
          </label>
          {allBooks.length === 0 ? (
            <p style={{ color: "var(--text-secondary)", fontSize: "0.9rem", marginTop: 8 }}>
              {t("noBooksAvailable")}
            </p>
          ) : (
            <div className="subject-book-checklist">
              {allBooks.map((b) => (
                <label
                  key={b.id}
                  className={`subject-book-check-item${bookIds.includes(b.id) ? " checked" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={bookIds.includes(b.id)}
                    onChange={() => toggleBook(b.id)}
                    style={{ display: "none" }}
                  />
                  <span className="subject-book-check-icon">
                    {b.type === "pdf" ? "📄" : "📗"}
                  </span>
                  <span className="subject-book-check-name">{b.name}</span>
                  {bookIds.includes(b.id) && (
                    <span className="subject-book-checkmark">✓</span>
                  )}
                </label>
              ))}
            </div>
          )}
        </div>
        <div className="bcm-footer">
          <button className="bcm-btn-cancel" onClick={onClose}>{t("cancel")}</button>
          <button
            className="bcm-btn-create"
            disabled={!name.trim()}
            onClick={() => onSave(name, bookIds)}
          >
            {modal.mode === "create" ? t("createSubjectBtn") : t("saveChangesBtn")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function HomePage({
  onNavigate,
  onSignOut,
  billingStatus,
  onGoToBilling,
  onGoToAdminBilling,
  onOpenProfile,
  userInitial,
  // Book-related props
  books,
  htmlBooks,
  onOpenBook,
  onOpenHtmlBook,
  onCreateBook,
  onUploadBook,
  onDeleteHtmlBook,
  // Notes-related props
  notes = [],
  subfolders = [],
  folderOrder = [],
}) {
  const uploadInputRef = useRef(null);
  const { t, lang, toggleLang } = useLanguage();
  const FEATURES = t("features");
  const plan      = billingStatus?.plan;
  const planName  = billingStatus?.plan_name || "";
  const planColor = PLAN_COLORS[plan] || "#64748b";

  // Detect admin from billing status
  const isAdmin = billingStatus?.is_admin === true;

  // Delete-confirm state for HTML books
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const confirmDelete = (id) => setDeleteConfirm(String(id));
  const doDelete = () => {
    if (deleteConfirm && onDeleteHtmlBook) {
      onDeleteHtmlBook(deleteConfirm);
    }
    setDeleteConfirm(null);
  };

  // ── Subjects ─────────────────────────────────────────────────────────
  const [groupBySubjects, setGroupBySubjects] = useState(false);
  const [subjects, setSubjects] = useState(() => {
    try { return JSON.parse(localStorage.getItem("book-subjects") || "[]"); }
    catch { return []; }
  });
  const [expandedSubjectId, setExpandedSubjectId] = useState(null);
  const [subjectModal, setSubjectModal] = useState(null);

  const allBooks = React.useMemo(() => [
    ...(books || []).map((b) => ({
      id: String(b.id), name: b.filename, type: "pdf",
      meta: `${b.total_pages} pages · PDF`,
    })),
    ...(htmlBooks || []).map((b) => ({
      id: String(b.id), name: b.title, type: "html", meta: "HTML Book",
    })),
  ], [books, htmlBooks]);

  const hasBooks = allBooks.length > 0;

  const saveSubjects = (next) => {
    setSubjects(next);
    localStorage.setItem("book-subjects", JSON.stringify(next));
  };

  const openSubjectModal = (subj = null) => {
    setSubjectModal(
      subj
        ? { mode: "edit", id: subj.id, name: subj.name, bookIds: [...(subj.bookIds || [])] }
        : { mode: "create", id: null, name: "", bookIds: [] }
    );
  };

  const handleSaveSubject = (name, bookIds) => {
    if (!name.trim()) return;
    if (subjectModal.mode === "create") {
      const id = String(Date.now());
      saveSubjects([...subjects, { id, name: name.trim(), bookIds }]);
      setExpandedSubjectId(id);
    } else {
      saveSubjects(subjects.map((s) =>
        s.id === subjectModal.id ? { ...s, name: name.trim(), bookIds } : s
      ));
    }
    setSubjectModal(null);
  };

  const handleDeleteSubject = (id) => {
    saveSubjects(subjects.filter((s) => s.id !== id));
    if (expandedSubjectId === id) setExpandedSubjectId(null);
  };

  const renderBookCard = (b) => (
    <div key={b.id} className={`home-book-card${b.type === "html" ? " home-book-card--html" : ""}`}>
      <div className="home-book-icon">{b.type === "pdf" ? "📄" : "📗"}</div>
      <div className="home-book-info">
        <div className="home-book-name">{b.name}</div>
        <div className="home-book-meta">{b.meta}</div>
      </div>
      <div className="home-book-actions">
        <button
          className="home-book-open-btn"
          onClick={() => b.type === "pdf" ? onOpenBook(b.id) : onOpenHtmlBook(b.id)}
        >{t("open")}</button>
        {b.type === "html" && (
          deleteConfirm === b.id ? (
            <>
              <button className="home-book-del-confirm" onClick={doDelete}>{t("confirm")}</button>
              <button className="home-book-del-cancel" onClick={() => setDeleteConfirm(null)}>{t("cancel")}</button>
            </>
          ) : (
            <button className="home-book-del-btn" onClick={() => confirmDelete(b.id)} title="Delete">🗑</button>
          )
        )}
      </div>
    </div>
  );

  // ── Notes section ─────────────────────────────────────────────────────
  const [groupNotesByFolder, setGroupNotesByFolder] = useState(true);
  const [expandedFolderId, setExpandedFolderId] = useState(null);
  const [noteSearch, setNoteSearch] = useState("");

  const orderedFolders = React.useMemo(() => {
    const folderMap = {};
    subfolders.forEach((f) => { folderMap[f.id] = f; });
    const result = [];
    (folderOrder || []).forEach((id) => {
      if (id === "general") return;
      if (folderMap[id]) result.push(folderMap[id]);
    });
    // Append any folders not in folderOrder
    subfolders.filter(f => !f.parent).forEach((f) => {
      if (!result.find(r => String(r.id) === String(f.id))) result.push(f);
    });
    return result;
  }, [subfolders, folderOrder]);

  const hasNotes = notes.length > 0;

  const filteredNotes = React.useMemo(() => {
    const q = noteSearch.trim().toLowerCase();
    if (!q) return notes;
    return notes.filter((n) =>
      (n.title || "").toLowerCase().includes(q) ||
      (n.content || "").toLowerCase().includes(q)
    );
  }, [notes, noteSearch]);

  const getNotesForFolder = (folder) =>
    filteredNotes.filter((n) => n.folder === folder.id);

  const renderNoteCard = (note) => (
    <div key={note.id} className="home-book-card">
      <div className="home-book-icon">📝</div>
      <div className="home-book-info">
        <div className="home-book-name">{note.title || "Untitled"}</div>
        <div className="home-book-meta">{note.timestamp || ""}</div>
      </div>
      <div className="home-book-actions">
        <button
          className="home-book-open-btn"
          onClick={() => onNavigate("reader")}
        >{t("open")}</button>
      </div>
    </div>
  );

  // Trial expiry info
  const trialEndFormatted = React.useMemo(() => {
    if (!billingStatus?.trial_end) return null;
    try {
      return new Date(billingStatus.trial_end).toLocaleDateString(undefined, {
        month: "short", day: "numeric", year: "numeric",
      });
    } catch { return null; }
  }, [billingStatus]);

  return (
    <div className="home-page">
      {/* ── Hero ─────────────────────────────────────── */}
      <section className="home-hero">
        <div className="home-hero-bg" />
        <div className="home-hero-content">
          <h1 className="home-hero-title">
            <span className="home-hero-note">NOTE</span><span className="home-hero-a">MILA</span>
          </h1>
          <p className="home-hero-subtitle">{t("heroSubtitle")}</p>
          <p className="home-hero-tagline">
            {t("heroTagline").split("\n").map((line, i) => (
              <React.Fragment key={i}>{line}{i === 0 && <br />}</React.Fragment>
            ))}
          </p>
          <div className="home-hero-actions">
            <button className="home-btn home-btn-primary home-btn-lg" onClick={() => onNavigate("reader")}>
              {t("btnOpenReader")}
            </button>
            <button className="home-btn home-btn-outline home-btn-lg" onClick={() => onNavigate("live-translate")}>
              {t("btnLiveTranslation")}
            </button>
          </div>
        </div>

        {/* Top-right controls */}
        <div style={{ position: "absolute", top: 18, right: 18, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {/* Plan badge */}
          {billingStatus && (
            <button
              onClick={onGoToBilling}
              style={{
                background: planColor + "22",
                border: `1px solid ${planColor}66`,
                color: planColor,
                borderRadius: 20,
                padding: "5px 14px",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
                letterSpacing: 0.3,
              }}
              title={plan === "free_trial" ? `Trial ends ${trialEndFormatted}` : "Manage subscription"}
            >
              {plan === "free_trial" ? `${t("planBadgeTrial")} ${trialEndFormatted}` : `${planName} Plan`}
            </button>
          )}
          {/* Admin link */}
          {isAdmin && (
            <button
              onClick={onGoToAdminBilling}
              style={{
                background: "rgba(255,165,0,0.12)",
                border: "1px solid rgba(255,165,0,0.35)",
                color: "#d97706",
                borderRadius: 20,
                padding: "5px 14px",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {t("adminBtn")}
            </button>
          )}
          <button className="lang-pill-toggle lang-pill-toggle--light" onClick={toggleLang}>
            <span className={lang === "en" ? "lang-pill-option active" : "lang-pill-option"}>EN</span>
            <span className="lang-pill-divider">|</span>
            <span className={lang === "zh" ? "lang-pill-option active" : "lang-pill-option"}>中</span>
          </button>
          {onOpenProfile && (
            <button
              className="home-profile-btn"
              onClick={onOpenProfile}
              title="My Profile"
              aria-label="Open profile"
            >
              {userInitial || "?"}
            </button>
          )}
        </div>
      </section>

      {/* ── Features ─────────────────────────────── */}
      <section className="home-features">
        <h2 className="home-section-title">{t("studySmarter")}</h2>
        <div className="home-features-grid">
          {FEATURES.map((f) => (
            <div key={f.title} className="home-feature-card">
              <div className="home-feature-icon" style={{ background: f.color + "18" }}>
                <span>{f.icon}</span>
              </div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── My Books ─────────────────────────────── */}
      <section className="home-books-section" style={{ display: "none" }}>
        <div className="home-books-header">
          <h2 className="home-section-title" style={{ marginBottom: 0 }}>My Books</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {hasBooks && (
              <label className="books-toggle-row">
                <span className="books-toggle-label">Group by Subjects</span>
                <span className="toggle-switch-wrap">
                  <input
                    type="checkbox"
                    checked={groupBySubjects}
                    onChange={(e) => setGroupBySubjects(e.target.checked)}
                    style={{ display: "none" }}
                  />
                  <span className={`toggle-track${groupBySubjects ? " toggle-track--on" : ""}`} />
                </span>
              </label>
            )}
            <input
              ref={uploadInputRef}
              type="file"
              accept=".pdf"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file && onUploadBook) onUploadBook(file);
                e.target.value = "";
              }}
            />
            <button className="home-btn home-btn-secondary home-btn-sm" onClick={() => uploadInputRef.current?.click()}>
              📤 Upload Book
            </button>
            <button className="home-btn home-btn-primary home-btn-sm" onClick={onCreateBook}>
              + Create Book
            </button>
          </div>
        </div>

        {!hasBooks ? (
          <div className="home-books-empty">
            <p>You haven't created any books yet.</p>
            <button className="home-btn home-btn-accent" onClick={onCreateBook}>
              ✨ Create your first book
            </button>
          </div>
        ) : !groupBySubjects ? (
          /* Flat grid */
          <div className="home-books-grid">{allBooks.map(renderBookCard)}</div>
        ) : (
          /* Grouped by subjects */
          <>
            {subjects.length === 0 ? (
              <div className="home-books-empty" style={{ marginBottom: 20 }}>
                <p>No subjects yet. Create a subject to group your books.</p>
                <button className="home-btn home-btn-primary" onClick={() => openSubjectModal()}>
                  📚 Create your first subject
                </button>
              </div>
            ) : (
              <div className="subject-folders-grid">
                {subjects.map((subj, idx) => {
                  const gradient = FOLDER_GRADIENTS[idx % FOLDER_GRADIENTS.length];
                  const subjBooks = allBooks.filter((b) => (subj.bookIds || []).includes(b.id));
                  const isActive = expandedSubjectId === subj.id;
                  return (
                    <div
                      key={subj.id}
                      className={`subject-folder-tile${isActive ? " subject-folder-tile--active" : ""}`}
                    >
                      <div
                        className="subject-folder-box"
                        style={{ background: gradient }}
                        onClick={() => setExpandedSubjectId(isActive ? null : subj.id)}
                      >
                        {Array.from({ length: 9 }, (_, i) => {
                          const b = subjBooks[i];
                          return b
                            ? <div key={i} className="subject-mini-book">{b.type === "pdf" ? "📄" : "📗"}</div>
                            : <div key={i} className="subject-mini-empty" />;
                        })}
                      </div>
                      <div className="subject-folder-name">{subj.name}</div>
                      <div className="subject-folder-tile-actions">
                        <button
                          className="subject-tile-btn"
                          title="Edit subject"
                          onClick={() => openSubjectModal(subj)}
                        >✏️</button>
                        <button
                          className="subject-tile-btn"
                          title="Delete subject"
                          onClick={() => handleDeleteSubject(subj.id)}
                        >🗑️</button>
                      </div>
                    </div>
                  );
                })}
                {/* New subject tile */}
                <div className="subject-folder-tile subject-new-tile" onClick={() => openSubjectModal()}>
                  <div className="subject-folder-box subject-folder-box--new">
                    <span className="subject-new-plus">+</span>
                  </div>
                  <div className="subject-folder-name" style={{ color: "var(--text-secondary)" }}>
                    New Subject
                  </div>
                </div>
              </div>
            )}

            {/* Expanded subject panel */}
            {expandedSubjectId && (() => {
              const subj = subjects.find((s) => s.id === expandedSubjectId);
              if (!subj) return null;
              const subjBooks = allBooks.filter((b) => (subj.bookIds || []).includes(b.id));
              return (
                <div className="subject-expanded-panel">
                  <div className="subject-expanded-header">
                    <span className="subject-expanded-title">{subj.name}</span>
                    <span className="subject-expanded-count">
                      {subjBooks.length} book{subjBooks.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  {subjBooks.length === 0 ? (
                    <p className="subject-empty-msg">
                      No books assigned yet.{" "}
                      <button className="subject-link-btn" onClick={() => openSubjectModal(subj)}>
                        Add books →
                      </button>
                    </p>
                  ) : (
                    <div className="home-books-grid">{subjBooks.map(renderBookCard)}</div>
                  )}
                </div>
              );
            })()}

            {/* Ungrouped books */}
            {(() => {
              const grouped = new Set(subjects.flatMap((s) => s.bookIds || []));
              const ungrouped = allBooks.filter((b) => !grouped.has(b.id));
              return ungrouped.length > 0 ? (
                <>
                  <div className="subject-ungrouped-title">📦 Ungrouped</div>
                  <div className="home-books-grid">{ungrouped.map(renderBookCard)}</div>
                </>
              ) : null;
            })()}
          </>
        )}
      </section>

      {/* Delete confirmation overlay */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="home-del-dialog" onClick={(e) => e.stopPropagation()}>
            <p>{t("deleteBookConfirm")}</p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
              <button className="home-book-del-cancel" onClick={() => setDeleteConfirm(null)}>{t("cancel")}</button>
              <button className="home-book-del-confirm" onClick={doDelete}>{t("delete")}</button>
            </div>
          </div>
        </div>
      )}

      {/* Subject modal */}
      {subjectModal && (
        <SubjectModal
          modal={subjectModal}
          allBooks={allBooks}
          onSave={handleSaveSubject}
          onClose={() => setSubjectModal(null)}
        />
      )}

      {/* ── My Notes (hidden) ────────────────────── */}
      {false && <section className="home-books-section">
        <div className="home-books-header">
          <h2 className="home-section-title" style={{ marginBottom: 0 }}>My Notes</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {hasNotes && (
              <input
                type="text"
                className="notes-search-input"
                placeholder="Search notes…"
                value={noteSearch}
                onChange={(e) => setNoteSearch(e.target.value)}
              />
            )}
            {hasNotes && (
              <label className="books-toggle-row">
                <span className="books-toggle-label">Group by Folders</span>
                <span className="toggle-switch-wrap">
                  <input
                    type="checkbox"
                    checked={groupNotesByFolder}
                    onChange={(e) => setGroupNotesByFolder(e.target.checked)}
                    style={{ display: "none" }}
                  />
                  <span className={`toggle-track${groupNotesByFolder ? " toggle-track--on" : ""}`} />
                </span>
              </label>
            )}
            <button
              className="home-btn home-btn-primary home-btn-sm"
              onClick={() => onNavigate("reader")}
            >
              Open Notes
            </button>
          </div>
        </div>

        {!hasNotes ? (
          <div className="home-books-empty">
            <p>You haven't created any notes yet.</p>
            <button className="home-btn home-btn-accent" onClick={() => onNavigate("reader")}>
              ✨ Start taking notes
            </button>
          </div>
        ) : !groupNotesByFolder ? (
          <div className="home-books-grid">{filteredNotes.map(renderNoteCard)}</div>
        ) : (
          <>
            <div className="subject-folders-grid">
              {orderedFolders.map((folder, idx) => {
                const folderNotes = getNotesForFolder(folder);
                const gradient = FOLDER_GRADIENTS[idx % FOLDER_GRADIENTS.length];
                const isActive = expandedFolderId === folder.id;
                return (
                  <div
                    key={folder.id}
                    className={`subject-folder-tile${isActive ? " subject-folder-tile--active" : ""}`}
                  >
                    <div
                      className="subject-folder-box"
                      style={{ background: gradient }}
                      onClick={() => setExpandedFolderId(isActive ? null : folder.id)}
                    >
                      {Array.from({ length: 9 }, (_, i) => {
                        const n = folderNotes[i];
                        return n
                          ? <div key={i} className="subject-mini-book">📝</div>
                          : <div key={i} className="subject-mini-empty" />;
                      })}
                    </div>
                    <div className="subject-folder-name">{folder.name}</div>
                  </div>
                );
              })}
            </div>

            {/* Expanded folder panel */}
            {expandedFolderId && (() => {
              const folder = orderedFolders.find((f) => f.id === expandedFolderId);
              if (!folder) return null;
              const folderNotes = getNotesForFolder(folder);
              return (
                <div className="subject-expanded-panel">
                  <div className="subject-expanded-header">
                    <span className="subject-expanded-title">{folder.name}</span>
                    <span className="subject-expanded-count">
                      {folderNotes.length} note{folderNotes.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  {folderNotes.length === 0 ? (
                    <p className="subject-empty-msg">No notes in this folder yet.</p>
                  ) : (
                    <div className="home-books-grid">{folderNotes.map(renderNoteCard)}</div>
                  )}
                </div>
              );
            })()}

            {/* Ungrouped notes (folder id not in orderedFolders) */}
            {(() => {
              const knownFolderIds = new Set(orderedFolders.map((f) => String(f.id)));
              const ungrouped = notes.filter((n) => {
                const fid = n.folder ? String(n.folder) : null;
                return fid === null || !knownFolderIds.has(fid);
              });
              return ungrouped.length > 0 ? (
                <>
                  <div className="subject-ungrouped-title">📦 Ungrouped</div>
                  <div className="home-books-grid">{ungrouped.map(renderNoteCard)}</div>
                </>
              ) : null;
            })()}
          </>
        )}
      </section>}
      {showTerms && <TermsModal onClose={() => setShowTerms(false)} />}
      {showPrivacy && <PrivacyPolicyModal onClose={() => setShowPrivacy(false)} />}
      {/* ── Footer ───────────────────────────────────── */}
      <AppFooter onShowTerms={() => setShowTerms(true)} onShowPrivacy={() => setShowPrivacy(true)} />
    </div>
  );
}
